/* eslint-disable hubspot-dev/no-private-classes */
import { BATTLESHIP, HEFFALUMP, KOALA } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { RTE_TOOLBAR_BUTTON_CLASS } from '../components/StyledTextToolbarButton';
import InsertMenuContainer from '../components/insertMenu/InsertMenuContainer';
import editorCapabilities, { AI_SUMMARY, BLOCKQUOTE, BOLD, CLEAR_FORMATTING, DOCUMENTS, FILE_ATTACHMENTS, FONT_SIZE, FONT_STYLE, INLINE_IMAGE, PERSONALIZATION_TOKEN, INVOICES, ITALIC, KNOWLEDGE_ARTICLE, LINK, LISTS, MEETINGS, PAYMENT_LINK, QUOTES, RENDER_SUBMIT_BUTTON, RENDER_TRAY, SIGNATURE, SNIPPETS, STRIKETHROUGH, TEMPLATES, TEXT_ALIGN, TEXT_COLOR, TEXT_HIGHLIGHT, TOOLBAR_CUSTOMIZATION, UNDERLINE, VIDEO_CONFERENCE_LINK, VIDEO_INSERTION, editorCapabilitiesCommands, isInsertCapability } from '../editorCapabilities';
import { LAYOUT_TYPES as INTERNAL_LAYOUT_TYPES } from '../editorCapabilities/toolbarCustomization';
import { getGlobalVars, getTracking } from './configUtils';
import { getDefaultFontPropsWithFallback } from './fontUtils';
import { jsx as _jsx } from "react/jsx-runtime";
export const LAYOUT_TYPES = INTERNAL_LAYOUT_TYPES;
export const SmallDivider = styled.div.withConfig({
  displayName: "buildCapabilitiesFromConfig__SmallDivider",
  componentId: "sc-1ug0iah-0"
})(["border-right:1px solid ", ";height:24px;margin:auto 4px;vertical-align:middle;display:inline-block;"], KOALA);
export const DropdownContentWrapper = styled.div.withConfig({
  displayName: "buildCapabilitiesFromConfig__DropdownContentWrapper",
  componentId: "sc-1ug0iah-1"
})(["@media only screen and (max-width:544px){.size-styles-dropdown,.font-styles-dropdown{max-width:80px;}.size-styles-dropdown{margin-top:0 !important;margin-left:5px;}}"]);
export const DropdownWrapper = styled.div.withConfig({
  displayName: "buildCapabilitiesFromConfig__DropdownWrapper",
  componentId: "sc-1ug0iah-2"
})([".inbox-text-formatting{width:22px;}@media only screen and (max-width:544px){.private-button--default{width:auto !important;}}"]);
export const SpacerEl = styled.div.withConfig({
  displayName: "buildCapabilitiesFromConfig__SpacerEl",
  componentId: "sc-1ug0iah-3"
})(["flex-grow:1;"]);
const DropdownEl = ({
  entry,
  renderComponentForEntry
}) => {
  const [linkedOpen, setLinkedOpen] = useState(false);
  const onOpenChangeHandler = useCallback(evt => setLinkedOpen(evt.target.value), []);
  const renderContent = () => {
    return /*#__PURE__*/_jsx(DropdownContentWrapper, {
      className: "p-x-2 p-top-2",
      children: entry.children.map(child => renderComponentForEntry(child, true))
    });
  };
  const innerReactElement = /*#__PURE__*/_jsx(UIDropdown, {
    buttonText: entry.buttonText,
    buttonClassName: `p-x-1 ${RTE_TOOLBAR_BUTTON_CLASS} ${entry.key}`,
    buttonUse: "link",
    caretRenderer: entry.caretRenderer,
    Content: renderContent,
    minMenuWidth: 0,
    placement: "top right",
    onOpenChange: entry.tooltip ? onOpenChangeHandler : undefined
  });
  if (entry.tooltip) {
    return /*#__PURE__*/_jsx(DropdownWrapper, {
      children: /*#__PURE__*/_jsx(UITooltip, {
        title: entry.tooltip,
        open: linkedOpen ? false : undefined,
        children: innerReactElement
      })
    });
  } else {
    return /*#__PURE__*/_jsx(DropdownWrapper, {
      children: innerReactElement
    });
  }
};
const INSERT_CAPABILITIES = [KNOWLEDGE_ARTICLE, SNIPPETS, VIDEO_INSERTION, MEETINGS, DOCUMENTS, QUOTES, PAYMENT_LINK, TEMPLATES, INVOICES, SIGNATURE];
const defaultToolbarLayout = [BOLD, ITALIC, UNDERLINE, CLEAR_FORMATTING, {
  key: 'extra-fonts-dropdown',
  layoutType: LAYOUT_TYPES.DROPDOWN,
  buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "hubspot-prosemirror.buttons.styleDropdown"
  }),
  children: [FONT_STYLE, FONT_SIZE, TEXT_COLOR, TEXT_HIGHLIGHT, STRIKETHROUGH, {
    layoutType: LAYOUT_TYPES.DIVIDER,
    key: 'dropdown-divider-1'
  }, BLOCKQUOTE, LISTS, TEXT_ALIGN]
}, {
  layoutType: LAYOUT_TYPES.DIVIDER,
  key: 'layout-divider-key-1'
}, LINK, editorCapabilities.EMOJI, editorCapabilities.HORIZONTAL_RULE, INLINE_IMAGE, KNOWLEDGE_ARTICLE, SNIPPETS, MEETINGS, TEMPLATES, {
  key: 'prosemirror-insert-menu',
  layoutType: LAYOUT_TYPES.INSERT_MENU,
  children: INSERT_CAPABILITIES
}, VIDEO_CONFERENCE_LINK, FILE_ATTACHMENTS, editorCapabilities.CONTENT_ASSISTANT, AI_SUMMARY, editorCapabilities.AI_ASSISTANT, VIDEO_INSERTION, {
  layoutType: LAYOUT_TYPES.SPACER,
  key: 'spacer-key-1'
}, RENDER_TRAY, RENDER_SUBMIT_BUTTON, PAYMENT_LINK, INVOICES, PERSONALIZATION_TOKEN];
const inboxLayout = [{
  key: 'inbox-text-formatting',
  layoutType: LAYOUT_TYPES.DROPDOWN,
  buttonText: /*#__PURE__*/_jsx(UIIcon, {
    name: "textColor",
    color: HEFFALUMP
  }),
  caretRenderer: () => null,
  children: [BOLD, ITALIC, UNDERLINE, CLEAR_FORMATTING, FONT_STYLE, FONT_SIZE, TEXT_COLOR, TEXT_HIGHLIGHT, STRIKETHROUGH, BLOCKQUOTE, LISTS, TEXT_ALIGN]
}, editorCapabilities.EMOJI, LINK, {
  layoutType: LAYOUT_TYPES.DIVIDER,
  key: 'inbox-layout-divider',
  style: {
    width: 1,
    backgroundColor: BATTLESHIP,
    border: 'none'
  }
}, INLINE_IMAGE, FILE_ATTACHMENTS, editorCapabilities.CONTENT_ASSISTANT, AI_SUMMARY, editorCapabilities.AI_ASSISTANT, {
  key: 'prosemirror-insert-menu',
  layoutType: LAYOUT_TYPES.INSERT_MENU,
  children: INSERT_CAPABILITIES
}, {
  layoutType: LAYOUT_TYPES.SPACER,
  key: 'spacer-key-1'
}, RENDER_TRAY, RENDER_SUBMIT_BUTTON];
export function buildToolbarFromConfig(editorConfig, providedToolbarLayout) {
  const globalVars = getGlobalVars(editorConfig);
  const tracking = getTracking(editorConfig);
  const toolbarLayout = editorConfig[TOOLBAR_CUSTOMIZATION] || providedToolbarLayout || (globalVars.useInboxLayout ? inboxLayout : defaultToolbarLayout);
  const toolbar = [];
  const hasChildren = entry => entry.children.some(child => editorConfig[child]);
  const renderComponentForEntry = (entry, isChild, extraProps) => {
    if (typeof entry === 'string') {
      if (editorConfig[entry]) {
        if (INSERT_CAPABILITIES.includes(entry) && globalVars.useInsertMenu && !isChild) {
          return undefined;
        }
        const CapabilityComp = editorCapabilitiesCommands[entry];
        return /*#__PURE__*/_jsx(CapabilityComp, Object.assign({}, editorConfig[entry], globalVars, getDefaultFontPropsWithFallback(editorConfig), extraProps), entry);
      }
    } else if (typeof entry === 'function') {
      const CustomCommand = entry;
      return /*#__PURE__*/_jsx(CustomCommand, {});
    } else {
      // is a layout object
      switch (entry.layoutType) {
        case LAYOUT_TYPES.SPACER:
          {
            return /*#__PURE__*/_jsx(SpacerEl, {
              "data-test-id": "spacer"
            }, entry.key);
          }
        case LAYOUT_TYPES.DROPDOWN:
          {
            return hasChildren(entry) ? /*#__PURE__*/_jsx(DropdownEl, {
              renderComponentForEntry: renderComponentForEntry,
              entry: entry
            }, entry.key) : undefined;
          }
        case LAYOUT_TYPES.DIVIDER:
          {
            return /*#__PURE__*/_jsx(SmallDivider, {
              style: entry.style,
              "data-test-id": "divider"
            }, entry.key);
          }
        case LAYOUT_TYPES.INSERT_MENU:
          {
            if (!globalVars.useInsertMenu || !hasChildren(entry)) {
              return undefined;
            }
            const insertMenuChildren = entry.children.filter(isInsertCapability);
            return /*#__PURE__*/_jsx(InsertMenuContainer, {
              editorConfig: editorConfig,
              insertTypeOrder: insertMenuChildren,
              onOpen: () => tracking.onOpenInsertPopover && tracking.onOpenInsertPopover()
            }, entry.key);
          }
        default:
          break;
      }
    }
    return undefined;
  };
  for (const layoutEntry of toolbarLayout) {
    toolbar.push(renderComponentForEntry(layoutEntry));
  }
  return toolbar.filter(x => !!x);
}
export const buildCapabilitiesFromConfig = buildToolbarFromConfig;